/* eslint-disable react/prop-types */
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  EMAIL_CONFIRMED_OPTIONS,
} from "../mailToolConstants";
import { getOptionLabel, getOptionValue } from "../mailToolUtils";

const MENU_PROPS = {
  PaperProps: {
    sx: { maxHeight: 360 },
  },
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const isSelected = (selected, value) =>
  asArray(selected).some((item) => String(item) === String(value));

const getSelectedLabels = (selected, options) => {
  const selectedValues = asArray(selected);
  if (selectedValues.length === 0) return "Όλα";

  const labels = selectedValues.map((value) => {
    const option = options.find(
      (item) => String(getOptionValue(item)) === String(value)
    );
    return option ? getOptionLabel(option) : String(value);
  });

  return labels.length <= 2 ? labels.join(", ") : `${labels.length} επιλεγμένα`;
};

const RecipientFilters = ({
  filters,
  onChange,
  onClear,
  onSearch,
  seasons = [],
  applicationTypes = [],
  genders = [],
  applicationStatuses = [],
  loading,
  disabled,
  hideTitle = false,
}) => {
  const handleChange = (field) => (event) => {
    onChange({ ...filters, [field]: event.target.value });
  };

  const handleMultiChange = (field) => (event) => {
    const value = event.target.value;
    onChange({
      ...filters,
      [field]: typeof value === "string" ? value.split(",") : value,
    });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent={hideTitle ? "flex-end" : "space-between"}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        {!hideTitle && (
          <Typography variant="h6" fontWeight={700}>
            Φίλτρα παραληπτών
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearIcon />}
            onClick={onClear}
            disabled={disabled || loading}
          >
            Καθαρισμός
          </Button>
          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                <SearchIcon />
              )
            }
            onClick={onSearch}
            disabled={disabled || loading}
          >
            Αναζήτηση παραληπτών
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={1.25}>
        <Grid item xs={12} md={4} lg={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Περίοδος"
            value={filters.seasonIds || []}
            onChange={handleMultiChange("seasonIds")}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) => getSelectedLabels(selected, seasons),
              MenuProps: MENU_PROPS,
            }}
          >
            {seasons.map((season) => {
              const id = getOptionValue(season);
              return (
                <MenuItem key={id} value={id}>
                  <Checkbox
                    size="small"
                    checked={isSelected(filters.seasonIds, id)}
                  />
                  <ListItemText primary={getOptionLabel(season)} />
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4} lg={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Τύπος αίτησης"
            value={filters.applicationTypeIds || []}
            onChange={handleMultiChange("applicationTypeIds")}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) =>
                getSelectedLabels(selected, applicationTypes),
              MenuProps: MENU_PROPS,
            }}
          >
            {applicationTypes.map((type) => {
              const id = getOptionValue(type);
              return (
                <MenuItem key={id} value={id}>
                  <Checkbox
                    size="small"
                    checked={isSelected(filters.applicationTypeIds, id)}
                  />
                  <ListItemText primary={getOptionLabel(type)} />
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4} lg={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Κατάσταση αίτησης"
            value={filters.applicationStatusIds || []}
            onChange={handleMultiChange("applicationStatusIds")}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) =>
                getSelectedLabels(selected, applicationStatuses),
              MenuProps: MENU_PROPS,
            }}
          >
            {applicationStatuses.map((status) => {
              const id = getOptionValue(status);
              return (
                <MenuItem key={id} value={id}>
                  <Checkbox
                    size="small"
                    checked={isSelected(filters.applicationStatusIds, id)}
                  />
                  <ListItemText primary={getOptionLabel(status)} />
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4} lg={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Φύλο"
            value={filters.genderId}
            onChange={handleChange("genderId")}
            disabled={disabled}
          >
            <MenuItem value="">Όλα</MenuItem>
            {genders.map((gender) => {
              const id = getOptionValue(gender);
              return (
                <MenuItem key={id} value={id}>
                  {getOptionLabel(gender)}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4} lg={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Επιβεβαίωση διεύθυνσης"
            value={filters.emailConfirmed}
            onChange={handleChange("emailConfirmed")}
            disabled={disabled}
          >
            <MenuItem value="">Όλα</MenuItem>
            {EMAIL_CONFIRMED_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={8} lg={2}>
          <TextField
            fullWidth
            size="small"
            label="Αναζήτηση"
            placeholder="Όνομα, διεύθυνση, ΑΜΚΑ, ΑΦΜ"
            value={filters.search}
            onChange={handleChange("search")}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RecipientFilters;
